import {User} from '@hconnect/apiclient'
import {Typography} from '@hconnect/uikit'
import {Box, Chip, Switch} from '@material-ui/core'
import {QueryObserverResult} from '@tanstack/react-query'
import React, {Dispatch} from 'react'
import {Trans} from 'react-i18next'

import {useTranslation} from '../../../hooks/useTranslation'
import {Roles} from '../../../layouts/Roles/Roles'
import {Permission} from '../../../modules/LoggedInUser'
import {RoleAssignment} from '../../../modules/ManageUsers.selectors'
import {ViewAuditButton} from '../ViewAuditButton'
import {useStyles} from './ManageUserRoles.styles'

type ManageUserRolesProps = {
  showAutoRoles: boolean
  setShowAutoRoles: Dispatch<React.SetStateAction<boolean>>
  accountNumber: string | null
  extraInfo: string | null
  refetchRoles: () => Promise<QueryObserverResult<RoleAssignment[] | null>>
  userCountry: string
  isLoading: boolean
  userId?: string
  roles?: RoleAssignment[] | null
  loggedInUserPermissions?: Permission[] | null
  loggedInUserProfile?: User | null
}
export const ManageUserRoles = ({
  accountNumber,
  extraInfo,
  refetchRoles,
  setShowAutoRoles,
  showAutoRoles,
  userId,
  userCountry,
  roles,
  isLoading,
  loggedInUserPermissions,
  loggedInUserProfile
}: ManageUserRolesProps) => {
  const {t} = useTranslation()
  const classes = useStyles()
  return (
    <Box my={4}>
      <Box className={classes.sectionHeader}>
        <Box display="flex" flexGrow={1} alignItems="center" alignSelf="stretch">
          <Typography variant="h2">{t('manageUser.roles')}</Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Box pr={1}>
            <ViewAuditButton
              auditType="roles"
              userIdentifier={userId ?? ''}
              userCountry={userCountry}
              loggedInUserPermissions={loggedInUserPermissions}
              loggedInUserProfile={loggedInUserProfile}
            />
          </Box>
          <Box mr={1}>
            <Trans i18nKey="manageUser.showAutoGenerated">
              Show <Chip className={classes.chipAuto} label={t('roleAssignment.auto')} /> generated
            </Trans>
          </Box>
          <Switch
            onChange={() => setShowAutoRoles((prev) => !prev)}
            checked={showAutoRoles}
            data-test-id="toggle-roles-auto-generated"
          />
        </Box>
      </Box>

      {accountNumber && (
        <Typography variant="body2" color="textPrimary">
          {t('manageUser.requestedAccountNumber', {accountNumber})}
        </Typography>
      )}
      {extraInfo && (
        <Typography variant="body2" color="textPrimary" gutterBottom>
          {t('manageUser.extraInfo', {extraInfo, interpolation: {escapeValue: false}})}
        </Typography>
      )}

      <Roles
        userId={userId}
        roleAssignments={roles}
        getRoles={() => refetchRoles()}
        showAutoGeneratedRoles={showAutoRoles}
        isLoading={isLoading}
        loggedInUserPermissions={loggedInUserPermissions}
        loggedInUserProfile={loggedInUserProfile}
      />
    </Box>
  )
}
